import { Box, Stack, Typography, useTheme } from "@mui/material";
import { StepComment } from "../dtos";
import {NoteAttachmentList} from "./note-attachments";

/**
 * §Decisions: why this step is open again.
 *
 * The finalized designs put a reason list at the top of every sent-back card — "Rejection reasons"
 * on Appraisal fees, "Violations found" on the Appraisal report, "Issues found" on Medical. In the
 * engine those are all ONE thing: the comment the reviewer was forced to write, because the
 * send-back action carries `requiresComment` (and, on a rework, `isReworkComment`). So this renders
 * the comment thread rather than three bespoke panels — a step the designs never covered gets the
 * same treatment for free.
 *
 * Rework comments lead and are styled as the alert; ordinary notes follow, quietly.
 */
const DecisionPanel = ({
  comments,
  /** REWORK_REQUIRED / REJECTED — drives whether the panel reads as an alert at all */
  attention,
}: {
  comments: StepComment[] | undefined;
  attention: boolean;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors.semantic;

  const all = comments ?? [];
  if (!all.length) return null;

  // newest first — the reason that reopened the step is the one to read
  const ordered = [...all].sort(
    (a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  // §ReworkResolution: the alert keys on UNRESOLVED rework comments — the reviewer's later
  // APPROVED completion stamps resolvedAt (never the agent's own resubmit), and a repeat
  // rejection arrives as a fresh unresolved comment. Resolved ones join the quiet thread.
  const rework = ordered.filter((comment) => comment.isReworkComment && !comment.resolvedAt);
  const notes = ordered.filter((comment) => !comment.isReworkComment || comment.resolvedAt);
  // an un-flagged thread on a step that IS sent back still carries the reason — treat the latest
  // note as the reason rather than showing nothing (older data predates the rework flag).
  const reasons = rework.length ? rework : attention ? notes.slice(0, 1) : [];

  const alert = reasons.length > 0;

  const when = (iso: string): string => {
    const date = new Date(iso);
    return Number.isNaN(date.getTime())
      ? ""
      : date.toLocaleString("en-GB", {
          day: "2-digit",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
        });
  };

  return (
    <Stack spacing={1.25} sx={{ mb: 2 }}>
      {alert && (
        // §MobileQA (design 2026-08-09): every [Mobile] send-back frame — dev-compound
        // "Legal team decision", seller & unit "Legal team feedback", cf-additional-docs
        // "Feedback" — renders the reviewer's reason as a QUIET slot: a grey caption over the
        // reason, on the plain white panel, with no border, no tint and no warning glyph. The
        // step card already sits on the pale-red ground and already carries the filled red
        // status disc, so a second red-bordered box double-shouted the same alarm.
        <Box>
          <Typography
            sx={{
              // design group-caption token: Body/xs - Regular 11/16 Text/Secondary #636363
              fontSize: 11,
              lineHeight: "16px",
              fontWeight: 400,
              color: colors.text.secondary,
              mb: 0.25,
            }}
          >
            {reasons.length > 1 ? "Reasons this came back" : "Why this came back"}
          </Typography>
          <Stack spacing={1}>
            {reasons.map((comment) => (
              <Box key={comment.uid}>
                <Typography
                  sx={{
                    // Body/md - Regular 14/20; the old 13/19 matched no token in the set
                    fontSize: 14,
                    lineHeight: "20px",
                    color: colors.text.primary,
                  }}
                >
                  {comment.content}
                </Typography>
                <Typography
                  sx={{ fontSize: 11, lineHeight: "16px", color: colors.text.tertiary }}
                >
                  {comment.actorName || comment.actorType} ·{" "}
                  {when(comment.createdAt)}
                </Typography>
                <NoteAttachmentList attachments={comment.attachments} />
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* §Reasons-only (product ask, 2026-08-06): the panel is the ALERT surface. Ordinary and
          broadcast notes stay in the engine's history but no longer render a "notes" thread here. */}
    </Stack>
  );
};

export default DecisionPanel;
